import axios from '@/axios';
import i18n from "@/i18n";

const state = {
    isLoading: false,
    languages: [],
    currentLanguage: localStorage.getItem('language') || "10",
    currencies: [],
    currency: JSON.parse(localStorage.getItem("currency")) || null,
    isAuth: false
}

const getters = {
    currentLanguage: state => state.currentLanguage,
    currentLanguageName: ({ currentLanguage, languages }) => {
        if (currentLanguage) {
            const lang = languages.find((lang) => lang.id == currentLanguage);
            if (lang) {
                return lang.title;
            }
        }
        return '';
    },
    currentLanguageCode: ({ currentLanguage, languages }) => {
        if (currentLanguage) {
            const lang = languages.find((lang) => lang.id == currentLanguage);
            if (lang) {
                return lang.code;
            }
        }
        return '';
    },
    allLanguages: state => state.languages,
    currencies: state => state.currencies,
    currency: state => state.currency,
    currencyCode: ({ currency }) => currency ? currency.code : '',
    currencySymbol: ({ currency }) => currency ? currency.symbol : '',
    isAuthenticated: state => state.isAuth,
}

const actions = {
    fetchLanguages: async ({ commit, dispatch, state: { currentLanguage } }) => {
        try {
            commit('setLoading', true);
            const { data: { status, payload } } = await axios.get('languages');
            if (status === 'OK') {
                commit('setLanguages', payload);
                
                 const isValidLanguage = payload.some(lang => lang.id == currentLanguage);

                 if (!currentLanguage || !isValidLanguage) { 
                     await dispatch('changeLocale', payload[0].id);
                 } else {
                     await dispatch('changeLocale', currentLanguage);
                 }
            }
        } catch (e) {
            console.log(e);
        } finally {
            commit('setLoading', false);
        }
    },
    changeLocale: async ({ commit, getters }, localeId) => {
        if (localeId) {
            commit('setCurrentLanguage', localeId);
            const currentLocale = getters.currentLanguageCode;
            let localTranslations = i18n.global.getLocaleMessage(currentLocale) || {};
            
            try {
                const { data } = await axios.get('/localizations', {
                    params: { lang_id: localeId }
                });

                if (data) {
                    let translations = {};
                    
                    if (Array.isArray(data)) {
                        data.forEach(t => translations[Object.keys(t)[0]] = Object.values(t)[0]);
                    } else if (typeof data === 'object') {
                        translations = data;
                    }

                    i18n.global.setLocaleMessage(currentLocale, {
                        ...localTranslations,
                        ...translations
                    });
                    i18n.global.locale = currentLocale;
                }
            } catch (e) {
                console.log(e);
                i18n.global.setLocaleMessage(currentLocale, localTranslations);
            }
        }
    },
    fetchCurrencies: async ({ commit }) => {
        try {
            commit('setLoading', true);
            const { data: { status, payload } } = await axios.get('currencies');
            if (status === 'OK') {
                const currencies = payload.map(({ code, symbol }) => ({ code, symbol }));
                commit('setCurrencies', currencies);

                let storedCurrency = null;
                try {
                    storedCurrency = JSON.parse(localStorage.getItem("currency"));
                } catch (e) {
                    console.error('Invalid currency in localStorage:', e);
                    localStorage.removeItem("currency");
                }

                // Validate the stored currency against API response
                const validCurrency = storedCurrency && currencies.find(({ code }) => code === storedCurrency.code);
                
                if (validCurrency) {
                    // Update stored currency with fresh symbol from API
                    const currencyFromApi = currencies.find(({ code }) => code === storedCurrency.code);
                    commit('setCurrency', currencyFromApi);
                } else {
                    // Use first currency from API as default
                    commit('setCurrency', currencies[0]);
                }
            }
        } catch (e) {
            console.log(e);
        } finally {
            commit('setLoading', false);
        }
    },
    changeCurrency({ commit, state }, newCurrencyCode) {
        const newCurrency = state.currencies.find(({ code }) => code === newCurrencyCode);
        if (newCurrency) {
            commit('setCurrency', newCurrency);
        }
    },
    checkAuth: async ({ commit }) => {
        try {
            const { data } = await axios.get("is-auth");
            commit('setAuth', data.authenticated);
            return data.authenticated;
        } catch (error) {
            commit('setAuth', false);
            return false;
        }
    },
}

const mutations = {
    setLoading: (state, loading) => (state.isLoading = loading),
    setLanguages: (state, languages) => (state.languages = languages),
    setCurrencies: (state, currencies) => (state.currencies = currencies),
    setCurrency: (state, currency) => {
        if (currency) {
            localStorage.setItem('currency', JSON.stringify(currency));
        } else {
            localStorage.removeItem('currency');
        }
        state.currency = currency;
    },
    setCurrentLanguage: (state, language) => {
        state.currentLanguage = language;
        localStorage.setItem('language', language);
    },
    setAuth: (state, value) => {
        state.isAuth = value;
    }
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
}
